/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import {
  FileSpreadsheet,
  KeyRound,
  CircleDollarSign,
  Funnel,
  MapPin,
  Rocket,
  User,
  Calendar,
  LayoutDashboard,
  Bell,
  Settings,
  Globe,
  Search,
  Share2,
} from 'lucide-react';
import { TagIcon } from '@/shared/ui/components/basic/icons';

import {
  FF_APP_CALENDAR,
  FF_APP_LAUNCHES,
  FF_APP_FACEBOOK,
  FF_APP_NOTIFICATIONS,
  FF_APP_AGENCY_SITE,
  FF_APP_PORTALS_INTEGRATION,
  FF_APP_PARTNERSHIPS,
  FF_APP_FUNNEL,
  FF_APP_LEASE,
  FF_APP_SALE,
  FF_APP_SPREADSHEETS,
} from '@/shared/utils/feature-flag';
import type { AgencyNavItem } from './agency-nav.types';

const DESKTOP_ICON = 20;
const MOBILE_ICON = 23;

/**
 * The single source of truth for agency navigation. Add or remove an item here
 * and both desktop and mobile update consistently.
 */
export const AGENCY_NAV_ITEMS: AgencyNavItem[] = [
  {
    id: 'dashboard',
    segment: 'dashboard',
    labelKey: 'sidebar.dashboard',
    icon: <LayoutDashboard width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <LayoutDashboard width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 0,
    desktopPlacement: 'nav',
    mobileOrder: 0,
    mobilePlacement: 'center',
  },
  {
    id: 'agenda',
    segment: 'agenda',
    labelKey: 'sidebar.agenda',
    icon: <Calendar width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <Calendar width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 3,
    desktopPlacement: 'nav',
    mobileOrder: 2,
    mobilePlacement: 'tab',
    isEnabled: () => FF_APP_CALENDAR,
  },
  {
    id: 'properties',
    segment: 'properties',
    labelKey: 'sidebar.properties',
    icon: <TagIcon width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <TagIcon width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 1,
    desktopPlacement: 'nav',
    mobileOrder: 0,
    mobilePlacement: 'tab',
  },
  {
    id: 'clients',
    segment: 'clients',
    labelKey: 'sidebar.clients',
    icon: <User width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <User width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 2,
    desktopPlacement: 'nav',
    mobileOrder: 1,
    mobilePlacement: 'tab',
  },
  {
    id: 'attendances',
    segment: 'attendances',
    labelKey: 'sidebar.attendances',
    icon: <Funnel width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <Funnel width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 2.5,
    desktopPlacement: 'nav',
    mobileOrder: 1.5,
    mobilePlacement: 'more',
    isEnabled: () => FF_APP_FUNNEL,
  },
  {
    id: 'leases',
    segment: 'leases',
    labelKey: 'sidebar.leases',
    icon: <KeyRound width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <KeyRound width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 6.5,
    desktopPlacement: 'nav',
    mobileOrder: 5.5,
    mobilePlacement: 'more',
    // Double gate [Fase 7]: the area is part of the funnel navigation AND
    // depends on the lease module being on, which is a separate rollout step.
    isEnabled: () => FF_APP_FUNNEL && FF_APP_LEASE,
  },
  {
    id: 'sales',
    segment: 'sales',
    labelKey: 'sidebar.sales',
    icon: <CircleDollarSign width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <CircleDollarSign width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 6.6,
    desktopPlacement: 'nav',
    mobileOrder: 5.6,
    mobilePlacement: 'more',
    // Double gate [Plano 4]: part of the funnel navigation, and its own
    // rollout step. Sits next to Locações because they are the two halves
    // of the same story -- a deal closes and becomes one or the other.
    isEnabled: () => FF_APP_FUNNEL && FF_APP_SALE,
  },
  {
    id: 'visits',
    segment: 'visits',
    labelKey: 'sidebar.visits',
    icon: <MapPin width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <MapPin width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 4,
    desktopPlacement: 'nav',
    mobileOrder: 3,
    mobilePlacement: 'more',
  },
  {
    id: 'launches',
    segment: 'launches',
    labelKey: 'sidebar.launches',
    icon: <Rocket width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <Rocket width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 7,
    desktopPlacement: 'nav',
    mobileOrder: 6,
    mobilePlacement: 'more',
    isEnabled: () => FF_APP_LAUNCHES,
  },
  {
    id: 'spreadsheets',
    segment: 'spreadsheets',
    labelKey: 'sidebar.spreadsheets',
    icon: <FileSpreadsheet width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <FileSpreadsheet width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 7.5,
    desktopPlacement: 'nav',
    mobileOrder: 6.5,
    mobilePlacement: 'more',
    isEnabled: () => FF_APP_SPREADSHEETS,
  },
  {
    id: 'partnerships',
    segment: 'partnerships',
    labelKey: 'sidebar.partnerships',
    icon: <Search width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <Search width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 8,
    desktopPlacement: 'nav',
    mobileOrder: 7,
    mobilePlacement: 'more',
    isEnabled: () => FF_APP_PARTNERSHIPS,
  },
  {
    id: 'agency-site',
    segment: 'agency-site',
    labelKey: 'sidebar.agency_site',
    icon: <Globe width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <Globe width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 10,
    desktopPlacement: 'nav',
    mobileOrder: 11,
    mobilePlacement: 'more',
    isEnabled: () => FF_APP_AGENCY_SITE,
  },
  {
    id: 'publications-social',
    segment: 'publications',
    labelKey: 'sidebar.publications_social',
    icon: <Share2 width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <Share2 width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 9,
    desktopPlacement: 'nav',
    mobileOrder: 8,
    mobilePlacement: 'more',
    isEnabled: () => FF_APP_PORTALS_INTEGRATION || FF_APP_FACEBOOK,
  },
  {
    id: 'notifications',
    segment: 'notifications',
    labelKey: 'sidebar.notifications',
    icon: <Bell width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <Bell width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 0,
    desktopPlacement: 'bottom',
    mobileOrder: 9,
    mobilePlacement: 'more',
    isEnabled: () => FF_APP_NOTIFICATIONS,
  },
  {
    id: 'settings',
    segment: 'settings',
    labelKey: 'sidebar.settings',
    icon: <Settings width={DESKTOP_ICON} height={DESKTOP_ICON} />,
    mobileIcon: <Settings width={MOBILE_ICON} height={MOBILE_ICON} />,
    desktopOrder: 1,
    desktopPlacement: 'bottom',
    mobileOrder: 10,
    mobilePlacement: 'more',
    requiredPermission: 'view_agency_settings',
  },
];
